import React, { useState, useEffect } from 'react';
import { useLogto } from '@logto/react';
import { apiGet, apiDelete } from '../utils/api';
import { Settings, X, Clock, FileEdit, Inbox, Loader2, Trash2 } from 'lucide-react';
import { MMMConfig } from '../types/mmmConfig';
import API_ENDPOINTS from '../config/apiConfig';

interface SavedConfig {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

interface SavedConfigsBrowserProps {
  onSelectConfig: (config: MMMConfig, configName: string) => void;
  onClose: () => void;
}

export const SavedConfigsBrowser: React.FC<SavedConfigsBrowserProps> = ({
  onSelectConfig,
  onClose,
}) => {
  const [configs, setConfigs] = useState<SavedConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loadingConfigId, setLoadingConfigId] = useState<string | null>(null);
  const logto = useLogto();
  const { getIdTokenClaims } = logto;
  const [userId, setUserId] = useState<string>('');

  useEffect(() => {
    const fetchUser = async () => {
      const claims = await getIdTokenClaims();
      if (claims) setUserId(claims.sub);
    };
    fetchUser();
  }, [getIdTokenClaims]);

  useEffect(() => {
    if (userId) loadConfigs();
  }, [userId]);

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<{ success: boolean; configs: SavedConfig[] }>(
        API_ENDPOINTS.CONFIG.LIST,
        logto
      );

      if (data.success) {
        setConfigs(data.configs);
      } else {
        setError('Failed to load configurations');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConfig = async (configId: string, configName: string) => {
    setLoadingConfigId(configId);
    try {
      const data = await apiGet<{ success: boolean; config: MMMConfig }>(
        API_ENDPOINTS.CONFIG.GET(configId),
        logto
      );

      if (data.success && data.config) {
        onSelectConfig(data.config, configName);
      } else {
        setError('Failed to load configuration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading configuration');
    } finally {
      setLoadingConfigId(null);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    setDeletingId(configId);
    try {
      await apiDelete(
        API_ENDPOINTS.CONFIG.DELETE(configId),
        logto
      );

      setConfigs(configs.filter((c) => c.id !== configId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting configuration');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" /> Saved Configurations
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-purple-800 rounded-full w-8 h-8 flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="mt-2 text-gray-600">Loading configurations...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-700">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {!loading && configs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Inbox className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p className="text-lg">No saved configurations yet</p>
              <p className="text-sm mt-2">Create and save a configuration to see it here</p>
            </div>
          )}

          {!loading && configs.length > 0 && (
            <div className="space-y-3">
              {configs.map((config) => (
                <div
                  key={config.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate text-lg">
                        {config.name}
                      </h3>
                      {config.description && (
                        <p className="text-sm text-gray-600 mt-1">{config.description}</p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Created: {formatDate(config.created_at)}</span>
                        <span className="flex items-center gap-1"><FileEdit className="w-3 h-3" /> Updated: {formatDate(config.updated_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleSelectConfig(config.id, config.name)}
                        disabled={loadingConfigId === config.id}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {loadingConfigId === config.id ? (
                          <><Loader2 className="w-4 h-4 inline mr-1 animate-spin" /> Loading...</>
                        ) : 'Load'}
                      </button>
                      <button
                        onClick={() => handleDelete(config.id)}
                        disabled={deletingId === config.id}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {deletingId === config.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
